import {WithStyledProps} from '@coolcolduk/ui-mui';
import {AttachFile, CalendarMonth, Email, Person} from '@mui/icons-material';
import {Box, Chip, Paper, Stack, Typography, styled} from '@mui/material';
import dayjs from 'dayjs';
import {ContactCategory, ContactMessage} from '../../__generated__/resolvers-types';

export interface ContactMessageListItemProps {
  message: ContactMessage;
  onClick: (message: ContactMessage) => void;
}

function ContactMessageListItemInternal({className, message, onClick}: WithStyledProps<ContactMessageListItemProps>) {
  const getCategoryColor = (category: ContactCategory) => {
    switch (category) {
      case ContactCategory.SUPPORT:
        return 'error';
      case ContactCategory.FEEDBACK:
        return 'info';
      case ContactCategory.FEATURE_REQUEST:
        return 'warning';
      case ContactCategory.GENERAL:
        return 'primary';
      case ContactCategory.OTHER:
        return 'default';
      default:
        return 'default';
    }
  };

  // Truncate message for summary view
  const truncatedMessage = message.message.length > 100 ? `${message.message.substring(0, 100)}...` : message.message;
  const daysAgo = dayjs().diff(dayjs(message.createdAt), 'day');

  return (
    <Paper
      className={className}
      sx={{p: 2, cursor: 'pointer', '&:hover': {bgcolor: 'action.hover'}}}
      onClick={() => onClick(message)}
    >
      <Stack spacing={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={1} sx={{flexGrow: 1}}>
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="medium">
                {message.name}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Email fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {message.email}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {dayjs(message.createdAt).format('YYYY-MM-DD HH:mm')}
              </Typography>
              {daysAgo <= 1 && <Chip label="day" color="primary" size="small" />}
              {daysAgo > 1 && daysAgo <= 7 && <Chip label="week" color="secondary" size="small" />}
            </Box>
          </Stack>
          <Chip
            label={message.category.replace('_', ' ')}
            color={getCategoryColor(message.category)}
            size="small"
            variant="outlined"
          />
        </Box>

        <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
          {truncatedMessage}
        </Typography>

        {message.attachments.length > 0 && (
          <Box display="flex" alignItems="center" gap={1}>
            <AttachFile fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {message.attachments.length} attachment{message.attachments.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export default styled(ContactMessageListItemInternal, {
  name: 'CcContactMessageListItem',
})<ContactMessageListItemProps>();
